import { useMemo } from 'react';

import clsx from 'clsx';

import { Hearts } from 'modules/Room/mobile/mobile-game-card/hearts';

import { Avatar } from 'shared/components/ui/Avatar/Avatar';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { useAccount } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { useGlobalStore } from 'shared/store/globalStore';
import { getChainIdFromUrl } from 'shared/utils/getChainIdFromUrl';
import { prettyTx } from 'shared/utils/pretty-tx';
import { getScannerLink } from 'shared/utils/scanner-link';

import loseIcon from 'assets/png/final/defeat.png?format=webp;avif&as=picture';
import victoryIcon from 'assets/png/final/victory.png?format=webp;avif&as=picture';

import './player-card.css';

interface Props {
  name: string;
  playerAddress: `0x${string}`;
  position: string;
  wins: number;
  isWinner?: boolean;
  isLooser?: boolean;
  winsTo: number;
}

export const PlayerCard = ({
  playerAddress,
  position,
  name,
  wins,
  winsTo,
  isWinner,
  isLooser,
}: Props) => {
  const { chainId } = useAccount();
  const isTablet = useMediaQuery('(max-width: 1200px)');
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const getRemainingWins = () => {
    return +winsTo - +wins;
  };
  const remainingWins = getRemainingWins();
  const screenAddress = useMemo(
    () =>
      prettyTx({
        address: playerAddress,
        leaderboardMap,
        startAddressLength: isTablet ? 4 : 6,
        endAddressLength: isTablet ? 3 : 4,
        discordUsernameLength: isTablet ? 10 : 20,
      }),
    [playerAddress, leaderboardMap, isTablet],
  );
  return (
    <div
      className={`lg:watch-game-card relative flex h-full max-h-[252px] w-full flex-col items-center justify-between rounded-[24px] transition lg:border lg:border-border lg:bg-background lg:bg-opacity-30 lg:py-4`}
    >
      <div className="flex flex-col items-center">
        <div className="flex w-full flex-row justify-center rounded-t-lg sm:border-0 lg:mb-[10px]">
          {playerAddress !== NULL_ADDRESS ? (
            <Avatar
              userAddress={playerAddress.toLowerCase() as `0x${string}`}
              className={`h-[44px] w-[54px] rounded-lg rounded-b-none sm:h-[134px] sm:min-w-[139px] lg:h-[64px] lg:min-w-[64px] lg:rounded-[12px]`}
            />
          ) : (
            <div className="flex h-[44px] items-center justify-center bg-deep-blue sm:h-[64px] sm:w-[64px]">
              <span className="text-[58px] text-blue">?</span>
            </div>
          )}
        </div>

        <div className="background-dark-background flex w-[54px] max-w-[283px] rounded-b-lg rounded-t-[0] border border-border px-[8px] sm:w-[unset] sm:min-w-[139px] sm:px-[18.5px] lg:block lg:rounded-lg lg:px-[32px] lg:py-[3px]">
          <a
            href={getScannerLink(playerAddress, chainId?.toString() || getChainIdFromUrl())}
            target="_blank"
            className="block w-full cursor-pointer text-center text-[8px] text-[#6A6B8A] sm:text-[20px] lg:text-yellow"
            style={{ opacity: playerAddress === NULL_ADDRESS ? 0 : 1 }}
          >
            {screenAddress}
          </a>
        </div>

        <div className="background-dark-background hidden rounded-bl-lg rounded-br-lg rounded-tl-none rounded-tr-none border border-t-0 border-border px-[32px] py-[3px] lg:flex">
          <span className="text-[14px] text-gray">{name}</span>
        </div>

        {((!isWinner && !isLooser) || isTablet) && (
          <div
            className={clsx(
              'mt-2 flex gap-[3px] text-white lg:mt-2 lg:gap-2',
              remainingWins !== null && 'animate-fast-appear',
              position === 'order-3' && 'scale-x-[-1] transform',
            )}
          >
            <Hearts
              wins={wins}
              winsTo={Number(winsTo)}
              prefix={name}
              opponent={position === 'order-3'}
              heartsSize="sm:h-[40px] sm:w-[40px] lg:h-[60px] lg:w-[60px]"
            />
          </div>
        )}

        {!isTablet && (isWinner || isLooser) && (
          <div className="mt-2 flex flex-col items-center">
            <OptimizedImage
              imageData={isWinner ? victoryIcon : loseIcon}
              alt={isWinner ? 'victory' : 'defeat'}
              className="h-[120px] w-[283px]"
              data-testid={isWinner ? 'victory-image' : 'defeat-image'}
            />
          </div>
        )}
      </div>
    </div>
  );
};
